import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

/**
 * Sort option for milestones (header + matrix). Plain string union for simplicity;
 * use a discriminated union if options later need metadata (e.g. sort direction).
 */
export type MilestoneSortPreferenceChoice = 'date' | 'name' | 'none';

/**
 * Composite keys used in the slideout, e.g. ``${phaseId}-${issueId}``,
 * ``nonphased-${issueId}``, ``inherited-${issueId}`` (all contain a hyphen).
 * Use this when typing a key variable; the map itself stays `MilestoneSortByState`.
 */
export type MilestoneSortCompositeKey = `${string}-${string}`;

/** In-memory sort overrides keyed by composite strings (see {@link MilestoneSortCompositeKey}). */
export type MilestoneSortByState = Record<
  string,
  MilestoneSortPreferenceChoice
>;

/** localStorage key for sticky "sort by date"; exported for tests and tooling. */
export const MILESTONE_SORT_PREFERENCE_STORAGE_KEY =
  'roadmap-rsd-milestones-sort-prefer-date';

function readStickyDateFromStorage(): boolean {
  try {
    return (
      localStorage.getItem(MILESTONE_SORT_PREFERENCE_STORAGE_KEY) === 'date'
    );
  } catch (err) {
    return false;
  }
}

export type UseMilestoneSortPreferenceResult = {
  getEffectiveSort: (
    perPhaseSort: Readonly<MilestoneSortByState>,
    phaseKey: string
  ) => MilestoneSortPreferenceChoice;
  /** Returns whether sticky storage reflects the choice (`date` / `none`). */
  persistIfDateChoice: (choice: MilestoneSortPreferenceChoice) => boolean;
  applyMilestoneSortAcrossIssue: (
    setMilestoneSortBy: Dispatch<SetStateAction<MilestoneSortByState>>,
    sortValue: MilestoneSortPreferenceChoice,
    phaseKeyForSinglePhaseOverride?: string
  ) => void;
};

/**
 * Sticky milestone sort for RSD slideouts: when the user picks **Date** in
 * Milestones "Sort by", that choice is stored in localStorage and reused as the
 * default for any phase key that has no in-memory override (e.g. new RSDs).
 * Selecting **Default** clears storage so the sticky preference turns off.
 * **Name** does not write to storage (unchanged from prior behavior).
 *
 * @param issueId - Current RSD issue id; used so **Date** / **Default** clear every
 *   in-memory phase key for that issue (`…-${issueId}`) and stay in sync across phases.
 */
export function useMilestoneSortPreference(
  issueId: string | undefined
): UseMilestoneSortPreferenceResult {
  const getEffectiveSort = useCallback(
    (
      perPhaseSort: Readonly<MilestoneSortByState>,
      phaseKey: string
    ): MilestoneSortPreferenceChoice => {
      const local = perPhaseSort[phaseKey];
      if (local !== undefined) return local;
      return readStickyDateFromStorage() ? 'date' : 'none';
    },
    []
  );

  /**
   * Call from sort handlers: `date` enables persistence; `none` (Default)
   * removes it so future RSDs no longer default to date sort.
   * @returns Whether localStorage now matches the intended sticky state for date/none.
   */
  const persistIfDateChoice = useCallback(
    (choice: MilestoneSortPreferenceChoice): boolean => {
      try {
        if (choice === 'date') {
          localStorage.setItem(MILESTONE_SORT_PREFERENCE_STORAGE_KEY, 'date');
          return (
            localStorage.getItem(MILESTONE_SORT_PREFERENCE_STORAGE_KEY) ===
            'date'
          );
        }
        if (choice === 'none') {
          localStorage.removeItem(MILESTONE_SORT_PREFERENCE_STORAGE_KEY);
          return (
            localStorage.getItem(MILESTONE_SORT_PREFERENCE_STORAGE_KEY) !==
            'date'
          );
        }
        return true;
      } catch (err) {
        return false;
      }
    },
    []
  );

  /**
   * **Date** / **Default** apply to all milestone sections for `issueId`: clears
   * every `milestoneSortBy` key ending with `-${issueId}` so localStorage (sticky
   * date) or default is consistent. **Name** keeps a single-phase override.
   */
  const applyMilestoneSortAcrossIssue = useCallback(
    (
      setMilestoneSortBy: Dispatch<SetStateAction<MilestoneSortByState>>,
      sortValue: MilestoneSortPreferenceChoice,
      phaseKeyForSinglePhaseOverride?: string
    ) => {
      if (!issueId) {
        persistIfDateChoice(sortValue);
        if (phaseKeyForSinglePhaseOverride) {
          setMilestoneSortBy(prev => ({
            ...prev,
            [phaseKeyForSinglePhaseOverride]: sortValue
          }));
        }
        return;
      }

      const suffix = `-${issueId}`;

      if (sortValue === 'date' || sortValue === 'none') {
        if (sortValue === 'none') {
          persistIfDateChoice('none');
          setMilestoneSortBy(prev => {
            const next = { ...prev };
            for (const k of Object.keys(next)) {
              if (k.endsWith(suffix)) {
                delete next[k];
              }
            }
            return next;
          });
          return;
        }

        const stickyOk = persistIfDateChoice('date');
        setMilestoneSortBy(prev => {
          const keysForIssue = Object.keys(prev).filter(k =>
            k.endsWith(suffix)
          );
          const next: MilestoneSortByState = { ...prev };

          if (stickyOk) {
            for (const k of keysForIssue) {
              delete next[k];
            }
            return next;
          }

          for (const k of keysForIssue) {
            next[k] = 'date';
          }
          const pk = phaseKeyForSinglePhaseOverride;
          if (pk && pk.endsWith(suffix)) {
            next[pk] = 'date';
          }
          return next;
        });
        return;
      }

      persistIfDateChoice(sortValue);

      if (!phaseKeyForSinglePhaseOverride) {
        return;
      }

      setMilestoneSortBy(prev => ({
        ...prev,
        [phaseKeyForSinglePhaseOverride]: sortValue
      }));
    },
    [persistIfDateChoice, issueId]
  );

  return useMemo(
    () => ({
      getEffectiveSort,
      persistIfDateChoice,
      applyMilestoneSortAcrossIssue
    }),
    [getEffectiveSort, persistIfDateChoice, applyMilestoneSortAcrossIssue]
  );
}
